import React, { useCallback, useContext } from 'react';
import { ServerErrorTypes } from 'types';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { convertUndefinedFields } from 'utils/helpers';
import CreateGameDeveloperState from './CreateDeveloperModal/createDeveloperModalContext/createDeveloperModalState/CreateDeveloperModalState';
import CreateDeveloperModal from './CreateDeveloperModal/CreateDeveloperModal';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	CreateGameContext,
	ICreateGameContext,
} from 'modules/Entertainment/Games/CreateGame/createGameState/createGameContext';
import { createGame } from 'backend/games';
import axios from 'axios';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		resetFields,
		showErrorModalWithInfo,
		changeTypeOfModalWindow,
		dataForSubmit,
		typeOfModalWindow,
		errorResponseData,
	} = useContext<ICreateGameContext>(CreateGameContext);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await createGame(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.GAME_APPLICATION_ID_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
					changeTypeOfModalWindow(null);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleResetFields = useCallback(() => {
		resetFields();
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
		changeTypeOfModalWindow(null);
	}, []);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow || !!errorResponseData}>
			<ModalToConfirm
				onConsentEvent={handleResetFields}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleResetFields}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'imgSizeExceeded'}
				description="fileSizeExceeded"
				translationOptions={{ size: '1MB' }}
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'fileSizeExceeded'}
				description="fileSizeExceeded"
				translationOptions={{ size: '150MB' }}
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'invalidFileFormat'}
				description="invalidFileFormatPhoto"
				translationOptions={{ formats: '.jpeg, .jpg, .png' }}
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'fileUploadError'}
				description="fileUploadError"
			/>
			<ModalSentError
				onConsentEvent={() => changeTypeOfModalWindow(null)}
				isVisible={typeOfModalWindow === 'someTranslationIsEmpty'}
				description="errorDefaultTranslation"
			/>
			{errorResponseData && (
				<ModalSentError
					onConsentEvent={handleCloseModalWithInfo}
					isVisible={true}
					description={errorResponseData.Type}
					translationOptions={{
						value: errorResponseData.Metadata.applicationId,
					}}
				/>
			)}
			<CreateGameDeveloperState>
				<CreateDeveloperModal isVisible={typeOfModalWindow === 'createDeveloper'} />
			</CreateGameDeveloperState>
		</ModalWrapper>
	);
};
